import {Component, ContentChild, ElementRef, Inject, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {FormControl, FormGroup, NG_VALUE_ACCESSOR} from '@angular/forms';
import {BaseFormComponent, Option, ValidationError} from 'nc-shared';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, map, pairwise, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {ValueFilterGroupStateMatcher} from '../value-filter-group-state-matcher';

@Component({
  selector: 'nc-quick-search',
  templateUrl: './nc-quick-search.component.html',
  styleUrls: ['./nc-quick-search.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR, useExisting: NcQuickSearchComponent, multi: true
  }]
})
export class NcQuickSearchComponent extends BaseFormComponent implements OnInit {
  @Input() displayOptionFunc: (item: Option) => string = (item: Option) => item && item.text;
  @Input() fetchAll: (term: string) => Observable<Array<Option>>;
  @Input() fetchSingle: (value: string) => Observable<Option>;
  @Input() debounceTime = 300;
  @Input() minCharNumberToExecuteSearch = 2;
  @Input() noResultsLabel = 'No results';
  @Input() textInputId: string;
  @Input() undoButtonId: string;
  @Input() resetButtonId: string;

  @ContentChild(TemplateRef) templateRef: TemplateRef<any>;
  @ViewChild('textInput') textInput: ElementRef;
  filterCtrl: FormControl;
  height: string;
  showUndo$: Observable<boolean>;
  private lastSelectedOption: Option;
  localFormGroup: FormGroup;
  stateMatcher = new ValueFilterGroupStateMatcher();
  private loadingSubject$ = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject$.asObservable();
  private showNoResultsSubject$ = new BehaviorSubject<boolean>(false);
  showNoResults$ = this.showNoResultsSubject$.asObservable();
  data$: Observable<Array<Option>>;
  currentValue: string;

  constructor(@Inject('ValidationErrorMap') protected errorMap: Record<string, ValidationError>) {
    super(errorMap);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.localFormGroup = new FormGroup({quickSearchFilter: new FormControl(), value: this.control});
    this.filterCtrl = this.localFormGroup.get('quickSearchFilter') as FormControl;

    this.showUndo$ = this.control.valueChanges.pipe(
      startWith(this.control.value),
      pairwise(),
      map((pair) => pair[0] && !pair[1])
    );

    this.data$ = this.filterCtrl.valueChanges.pipe(
      tap(() => this.showNoResultsSubject$.next(false)),
      debounceTime(this.debounceTime),
      distinctUntilChanged(),
      filter((value) => this.filterCtrl.enabled && typeof value === 'string'),
      map(term => term.toLowerCase()),
      tap(x => this.loadingSubject$.next(true)),
      switchMap(term => term.length > this.minCharNumberToExecuteSearch - 1 && this.fetchAll
        ? this.fetchAll(term).pipe(tap(x => this.showNoResultsSubject$.next(x.length === 0)))
        : of([])),
      tap(x => {
        this.loadingSubject$.next(false);
      }),
      tap(options => {
        if (options.length < 4 && options.length > 0) {
          this.height = (options.length * 50) + 'px';
        } else {
          this.height = '200px';
        }
      })
    );

    this.control.valueChanges.pipe(
      filter(value => this.lastSelectedOption?.value !== value)
    ).subscribe((value) => {
      this.filterCtrl.setValue(value);
      this.filterCtrl.enable();
      this.formGroup.updateValueAndValidity();
    });

    this.currentValue = this.control.value;

    if (this.fetchSingle) {
      this.control.valueChanges.pipe(
        startWith(this.control.value),
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter(value => value && this.lastSelectedOption?.value !== value),
        switchMap(value => this.fetchSingle(value))
      ).subscribe(option => this.onSelectOption(option));
    }
  }

  undo = () => {
    this.control.setValue(this.lastSelectedOption.value);
    this.filterCtrl.setValue(this.displayOptionFunc(this.lastSelectedOption));
    this.filterCtrl.disable();
    this.formGroup.updateValueAndValidity();
  }

  onSelectOption = (option: Option) => {
    this.lastSelectedOption = option;

    if (option.value !== this.control.value) {
      this.formGroup.markAsDirty();
    }

    this.control.setValue(option.value);
    this.filterCtrl.setValue(this.displayOptionFunc(option));
    this.filterCtrl.disable();

    if (this.textInput) {
      this.textInput.nativeElement.blur();
    }
  }

  onBlur = (e: FocusEvent) => {
    if (e.relatedTarget && (e.relatedTarget as HTMLElement).tagName === 'MAT-OPTION') {
      this.filterCtrl.markAsUntouched();
    }

    if (!this.control.value) {
      this.filterCtrl.setValue('');
    }

    this.formGroup.updateValueAndValidity();
  }

  reset = () => {
    this.control.reset();
    this.filterCtrl.enable();
    this.filterCtrl.setValue('');
    this.formGroup.updateValueAndValidity();
  }
}
